// Tek soru input dağıtıcısı — M15 D4.
// QuestionCard VE CompositeRenderer (parça başına) aynı bileşeni kullanır; eskiden
// FormFill içinde 9 dallı inline `&&` zinciriydi — exhaustive değildi, eksik tip
// sessizce boş kart render ederdi. `switch` + `assertNever`: QuestionType'a yeni
// değer eklendiğinde burası DERLEME hatası verir.
// Not (D8): `options` erişimi tipli okuyuculardan/renderer'ların kendi guard'larından
// geçer — assertNever yalnızca `type` dalını korur.

import type { QuestionSnapshot } from "@mahir/shared";
import { TextRenderer } from "./renderers/TextRenderer";
import { NumericRenderer } from "./renderers/NumericRenderer";
import { DateRenderer } from "./renderers/DateRenderer";
import { SingleRenderer } from "./renderers/SingleRenderer";
import { MultiRenderer } from "./renderers/MultiRenderer";
import { PercentRenderer } from "./renderers/PercentRenderer";
import { TableRenderer } from "./renderers/TableRenderer";
import { RepeaterRenderer } from "./renderers/RepeaterRenderer";
import { FileRenderer } from "./renderers/FileRenderer";
import { CompositeRenderer } from "./renderers/CompositeRenderer";

function assertNever(x: never): never {
  throw new Error(`Bilinmeyen soru tipi: ${String(x)}`);
}

interface Props {
  question: QuestionSnapshot;
  value: unknown;
  onChange: (v: unknown) => void;
}

export function QuestionInput({ question, value, onChange }: Props): JSX.Element {
  switch (question.type) {
    case "text":
      return (
        <TextRenderer
          question={question}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
        />
      );
    case "numeric":
      return (
        <NumericRenderer
          question={question}
          value={typeof value === "number" ? value : null}
          onChange={onChange}
        />
      );
    case "date":
      return (
        <DateRenderer
          value={typeof value === "string" ? value : null}
          onChange={onChange}
          options={question.options}
        />
      );
    case "single":
      return (
        <SingleRenderer
          question={question}
          value={
            value && typeof value === "object" && !Array.isArray(value) && "optionId" in value
              ? (value as { optionId: string; customInput?: string })
              : null
          }
          onChange={onChange}
        />
      );
    case "multi":
      return (
        <MultiRenderer
          question={question}
          value={
            value && typeof value === "object" && !Array.isArray(value) && "optionIds" in value
              ? (value as { optionIds: string[]; customInputs?: Record<string, string> })
              : null
          }
          onChange={onChange}
        />
      );
    case "percent":
      return (
        <PercentRenderer
          question={question}
          value={
            value && typeof value === "object" && !Array.isArray(value)
              ? (value as Record<string, number>)
              : null
          }
          onChange={onChange}
        />
      );
    case "table":
      return (
        <TableRenderer
          question={question}
          value={Array.isArray(value) ? (value as Record<string, unknown>[]) : []}
          onChange={onChange}
        />
      );
    case "repeater":
      return (
        <RepeaterRenderer
          question={question}
          value={Array.isArray(value) ? (value as Record<string, unknown>[]) : []}
          onChange={onChange}
        />
      );
    case "file":
      return (
        <FileRenderer
          question={question}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={onChange}
        />
      );
    case "composite":
      // Parça yazımları D6 gereği store'un fonksiyonel setValue'suna gider —
      // CompositeRenderer store'a kendisi bağlanır, buradaki onChange kullanılmaz.
      return <CompositeRenderer question={question} value={value} />;
    default:
      return assertNever(question.type);
  }
}
